import { useState } from 'react'
import { fbt } from 'fbtee'
import { Link, Link2Off, Share2, Loader2, Check, CalendarClock } from 'lucide-react'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { GroupRow } from '@/components/GroupRow'
import { cn } from '@/lib/utils'
import { prepareInvite, revokeEventInvites } from '@/lib/invites'
import { runMutation } from '@/lib/firestoreMutation'
import { getScheduleShareUrl } from '@/lib/utils'
import { copyLink, shareLink, canNativeShare } from '@/lib/share'
import { toastError } from '@/lib/toast'
import { classifyError } from '@/lib/errors'
import type { Group } from '@/types'

type Selection = { kind: 'personal' } | { kind: 'group'; groupId: string; name: string }

// "Share your schedule" sheet. First choice: your own picks, or a whole group's
// picks (all members). A group share lets whoever opens it see the group's
// schedule and join the group; a personal share shows just your picks. The invite
// is minted only on share, so it reflects the final choice.
export function ScheduleShareDialog({
  open,
  onOpenChange,
  eventId,
  eventName,
  ownerId,
  groups,
  setCount,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  eventId: string
  eventName: string
  ownerId: string
  groups: Group[]
  setCount: number
}) {
  const [selection, setSelection] = useState<Selection>({ kind: 'personal' })
  const [busy, setBusy] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const native = canNativeShare()

  // `consume` must reach its browser API (clipboard / share sheet) synchronously
  // — both need a live user gesture. So the invite id + URL are built up front
  // and the Firestore write runs alongside, never before, the gesture.
  function withLink(consume: (url: string) => Promise<unknown>) {
    setBusy(true)
    const { id, persist } = prepareInvite({
      ownerId,
      kind: selection.kind,
      eventId,
      groupId: selection.kind === 'group' ? selection.groupId : undefined,
      groupInviteCode: selection.kind === 'group' ? groups.find(g => g.id === selection.groupId)?.inviteCode : undefined,
    })
    const consumed = consume(getScheduleShareUrl(eventId, id))
    void Promise.allSettled([consumed, persist()]).then(([, write]) => {
      setBusy(false)
      if (write.status === 'rejected') {
        toastError(
          fbt("Couldn't create share link", 'Error toast when minting a schedule share link fails'),
          classifyError(write.reason),
        )
        return
      }
      onOpenChange(false)
    })
  }

  const copiedToast = () => fbt('Schedule link copied', 'Toast after copying a schedule share link')
  const copyErrorToast = () => fbt("Couldn't copy link", 'Error toast when copying a schedule share link fails')
  const trackParams = (method: 'native_share' | 'clipboard') => ({ method, event_id: eventId, kind: selection.kind })

  const doCopy = () =>
    withLink(url =>
      copyLink(url, {
        successToast: copiedToast(),
        errorToast: copyErrorToast(),
        event: 'schedule_shared',
        params: trackParams('clipboard'),
      }),
    )

  const doShare = () =>
    withLink(url =>
      shareLink(url, {
        text: fbt(
          'My ' + fbt.param('event', eventName) + ' schedule',
          'Share sheet message when sharing your festival schedule',
        ) as unknown as string,
        successToast: copiedToast(),
        errorToast: copyErrorToast(),
        event: 'schedule_shared',
        shareParams: trackParams('native_share'),
        copyParams: trackParams('clipboard'),
      }),
    )

  const revokeOld = async () => {
    setRevoking(true)
    try {
      await runMutation(
        fbt("Couldn't turn off links", 'Error toast when revoking schedule share links fails'),
        () => revokeEventInvites(ownerId, eventId),
      )
      toast.success(fbt('Old schedule links turned off', 'Toast after revoking previously shared schedule links'))
    } catch {
      // runMutation already toasted.
    } finally {
      setRevoking(false)
    }
  }

  const personalSelected = selection.kind === 'personal'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <fbt desc="Title of the share-your-schedule dialog">Share a schedule</fbt>
          </DialogTitle>
          <DialogDescription>
            <fbt desc="Description of what a shared schedule link does">
              Whoever opens the link can see the picks.
            </fbt>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <SectionLabel>
              <fbt desc="Section label above the option to share your own schedule">Just you</fbt>
            </SectionLabel>
            <ChoiceRow
              icon={<CalendarClock className="size-4" />}
              title={fbt('My schedule', 'Option to share your own schedule picks')}
              subtitle={fbt(
                fbt.param('count', setCount) + ' sets',
                'Count of the sharer own picks in the share dialog',
              )}
              selected={personalSelected}
              onClick={() => setSelection({ kind: 'personal' })}
            />
          </div>

          {groups.length > 0 && (
            <div className="space-y-1.5">
              <SectionLabel>
                <fbt desc="Section label above the group options in the share dialog">A group (everyone's picks)</fbt>
              </SectionLabel>
              <ul className="flex flex-col gap-2">
                {groups.map(g => {
                  const selected = selection.kind === 'group' && selection.groupId === g.id
                  return (
                    <li key={g.id}>
                      <GroupRow
                        group={g}
                        selected={selected}
                        ariaPressed={selected}
                        onClick={() => setSelection({ kind: 'group', groupId: g.id, name: g.name })}
                        trailing={hasImage => (
                          <span className={cn('flex items-center gap-2 shrink-0 text-xs', hasImage ? 'text-on-image/80' : 'text-muted-foreground')}>
                            {fbt(
                              fbt.param('count', g.memberIds.length) + ' members',
                              'Member count of a group in the share dialog',
                            )}
                            {selected && <Check className={cn('size-4', hasImage ? 'text-on-image' : 'text-primary')} />}
                          </span>
                        )}
                      />
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button onClick={doCopy} variant={native ? 'outline' : 'default'} className="flex-1 cursor-pointer" disabled={busy}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Link className="size-4" />}
            <fbt desc="Button to copy the schedule share link">Copy link</fbt>
          </Button>
          {native && (
            <Button onClick={doShare} className="flex-1 cursor-pointer" disabled={busy}>
              <Share2 className="size-4" />
              <fbt desc="Button to open the native share sheet for the schedule link">Share</fbt>
            </Button>
          )}
        </div>
        <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={revokeOld} disabled={busy || revoking}>
          {revoking ? <Loader2 className="size-4 animate-spin" /> : <Link2Off className="size-4" />}
          <fbt desc="Button in the schedule share dialog that revokes every schedule link the user already shared for this event">
            Turn off links I've shared
          </fbt>
        </Button>
      </DialogContent>
    </Dialog>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">{children}</p>
  )
}

function ChoiceRow({
  icon,
  title,
  subtitle,
  selected,
  onClick,
}: {
  icon: React.ReactNode
  title: React.ReactNode
  subtitle: React.ReactNode
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left cursor-pointer transition-colors',
        selected ? 'border-primary bg-primary/10' : 'hover:bg-muted/60',
      )}
    >
      <span className="text-muted-foreground">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium truncate">{title}</span>
        <span className="block text-xs text-muted-foreground">{subtitle}</span>
      </span>
      {selected && <Check className="size-4 text-primary shrink-0" />}
    </button>
  )
}
